import { PublicKey, TransactionInstruction } from '@solana/web3.js';
import { User, VelocityClient, isOrderExpired } from '@velocity-exchange/sdk';
import { createCancelOrdersIx } from '@velocity-exchange/common';
import { MAX_ORDER_COUNT } from 'src/constants/constants';
import { getChainClockTs } from 'src/hooks/useChainClockTs';
import { ORDER_EXPIRY_HIDE_BUFFER_SECONDS } from 'src/utils/orderExpiry';

/**
 * When placing `newOrderCount` order(s) would push the user past the 32-order on-chain
 * limit, returns a single instruction that cancels ALL currently-expired orders so
 * their slots are freed. The caller must `unshift` this to the FRONT of the placement
 * instructions so the cancels execute first, atomically making room within the same
 * transaction. Returns `undefined` when there's room or nothing to reclaim.
 *
 * Uses the true on-chain open-order count (`user.getOpenOrders()`, which includes
 * expired-but-uncleared orders that still occupy a slot) — NOT the UI's hidden list.
 */
export async function buildExpiredOrderCancelIx({
	velocityClient,
	user,
	newOrderCount,
	mainSignerOverride,
}: {
	velocityClient: VelocityClient;
	user: User;
	newOrderCount: number;
	mainSignerOverride?: PublicKey;
}): Promise<TransactionInstruction | undefined> {
	const chainTs = getChainClockTs();
	if (!chainTs) return undefined;

	const openOrders = user.getOpenOrders();
	if (openOrders.length + newOrderCount <= MAX_ORDER_COUNT) return undefined;

	const expiredOrderIds = openOrders
		.filter((order) =>
			isOrderExpired(order, chainTs - ORDER_EXPIRY_HIDE_BUFFER_SECONDS)
		)
		.map((order) => order.orderId);

	if (expiredOrderIds.length === 0) return undefined;

	return createCancelOrdersIx({
		velocityClient,
		user,
		orderIds: expiredOrderIds,
		mainSignerOverride,
	});
}
